// packages/web-ui/src/components/file-tree/flatten.ts
//
// Story W3-S5 AC #2.2 — flatten a hierarchical `FileTreeNode[]` into a
// `FlatTreeRow[]` honouring the consumer-provided expansion-state map.
// Pure function — no side effects, no allocations beyond the result array.
//
// Only rows whose ancestors are all expanded appear in the output. Roots
// always appear (depth 0). Children of a collapsed dir do NOT appear.

import type { FileTreeNode } from '../../api/clients/files.js';

export interface FlatTreeRow {
  /** The original tree node. */
  node: FileTreeNode;
  /** 0-based depth — 0 = root. */
  depth: number;
  /** Dirs only. `undefined` for files. */
  isExpanded?: boolean;
  /** Workspace-relative parent path; `undefined` for roots. */
  parentPath?: string;
}

export interface FlattenOptions {
  /**
   * Default expansion state for dirs whose path is NOT in the map.
   * Per AC #2.5 the default is `true` for top-level dirs (depth 0), `false`
   * otherwise. Pass `'all-collapsed'` or `'all-expanded'` to override.
   */
  defaultExpansion?: 'top-level' | 'all-collapsed' | 'all-expanded';
}

/**
 * Walk the tree and emit one `FlatTreeRow` per visible row. Top-level dirs
 * default to expanded; deeper dirs default to collapsed (mirrors VS Code).
 *
 * The `expansionState` map overrides the defaults. A `true` entry expands a
 * dir whose ancestors are all visible; a `false` entry collapses it.
 */
export function flattenTree(
  nodes: readonly FileTreeNode[],
  expansionState: ReadonlyMap<string, boolean> = new Map(),
  options: FlattenOptions = {},
): FlatTreeRow[] {
  const defaults = options.defaultExpansion ?? 'top-level';
  const out: FlatTreeRow[] = [];

  function isExpandedDefault(depth: number): boolean {
    if (defaults === 'all-expanded') return true;
    if (defaults === 'all-collapsed') return false;
    return depth === 0;
  }

  function walk(
    list: readonly FileTreeNode[],
    depth: number,
    parentPath: string | undefined,
  ): void {
    for (const node of list) {
      if (node.kind === 'directory') {
        const overridden = expansionState.get(node.path);
        const expanded = overridden ?? isExpandedDefault(depth);
        out.push({ node, depth, isExpanded: expanded, parentPath });
        if (expanded && node.children !== undefined && node.children.length > 0) {
          walk(node.children, depth + 1, node.path);
        }
      } else {
        out.push({ node, depth, parentPath });
      }
    }
  }

  walk(nodes, 0, undefined);
  return out;
}
